import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";

import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  QueryColumn,
  QueryColumnSortOrder,
  TargetColumnValue,
  TargetObjectData,
} from "@barthauer/abc/core/data";

import { DatePipe } from "@angular/common";
import {
  AbcPopupComponent,
  AbcSpeedDialActionComponent,
  _AbcContextMenuComponent,
  _AbcGridComponent,
} from "@barthauer/abc/ui-components";
import { DefaultIcons } from "@barthauer/custom/xmt-models";
import { DxDataGridModule } from "devextreme-angular";
import { Column } from "devextreme/ui/data_grid";
import { Subscription } from "rxjs";
import {
  NotificationMessage,
  ActionType,
  PermissionContext,
  ToastType,
} from "../../models/enumeration-list.model";
import { AppService } from "../../services/app.service";
import { DataService, ObjectKeys } from "../../services/data.service";
import { NotificationService } from "../../services/notification.service";
import { SharedModule } from "../../shared/shared.module";
import { CheckPermissionDirective } from "../../directives/check-permession-directive";

@Component({
  selector: "ubt-geods-roles",
  templateUrl: "./roles.component.html",
  styleUrls: ["./roles.component.scss"],
  imports: [
    SharedModule,
    _AbcGridComponent,
    DxDataGridModule,
    AbcSpeedDialActionComponent,
    _AbcContextMenuComponent,
    AbcPopupComponent,
    CheckPermissionDirective,
  ],
})
export class RolesComponent implements OnInit, OnDestroy {
  @ViewChild("rolesGrid") rolesGrid: _AbcGridComponent;
  @ViewChild("contextMenu") contextMenu: _AbcContextMenuComponent;
  @ViewChild("confirmDeletePopup") confirmDeletePopup: AbcPopupComponent;

  public availableRoles: Role[] = [];
  public isUpdating = false;
  private rowIndex: number;
  public columns: QueryColumn[] = [];
  public columnsHeader: Column[] = [
    {
      caption: this.dataService.res("Ubt-GridColumn-Name"),
      dataField: "Value",
      dataType: "string",
      visibleIndex: 0,
      sortOrder: "asc",
    },
    {
      type: "buttons",
      caption: "",
      alignment: "left",
      dataField: "",
      visibleIndex: 1,
      minWidth: 50,
      width: "50px",
    },
  ];
  public contextMenuItems: any[] = [
    {
      text: this.dataService.res("Ubt-Roles-Edit-Role"),
      onClick: () => {
        this.openEditDialog();
      },
    },
    {
      text: this.dataService.res("Ubt-Roles-Delete-Role"),
      onClick: () => {
        this.openRemoveDialog();
      },
    },
  ];
  public role: Role = new Role(null);
  public Action = ActionType;
  private subscriptions: Subscription[] = [];
  private OPath = "ref:ParentId='Personenfunktion/";
  private parentId: string;
  public noDataText = this.dataService.res("Ubt-No-Data-text");
  public treeListRole: String;
  public readonly exportFileName =
    this.dataService.res("Ubt-TreeList-Roles") +
    "_" +
    this.datePipe.transform(new Date(), "dd.MM.yyyy");
  public PermissionContext = PermissionContext;
  constructor(
    public dataService: DataService,
    public appService: AppService,
    private persistenceService: PersistenceService,
    private datePipe: DatePipe,
    private coreDataService: CoreDataService,
    private notificationService: NotificationService,
  ) {
    this.treeListRole = this.dataService.res("Ubt-TreeList-Roles");
  }

  public ngOnInit(): void {
    this.dataService.floatingButtonConfig();
    this.appService.showLoader(true);
    this.columns.push(
      this.coreDataService.createQueryColumn(
        "ParentId",
        "ParentId",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Id",
        "Id",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Value",
        "Value",
        QueryColumnSortOrder.None,
      ),
      this.coreDataService.createQueryColumn(
        "Description",
        "Description",
        QueryColumnSortOrder.None,
      ),
    );

    // this.OPath = this.OPath + localStorage.getItem('fixedCulture') === 'en' ? "1033/'" : "1031/'";
    this.OPath = this.OPath + "1031/'";

    this.subscriptions.push(
      this.dataService
        .readObjects<any>(ObjectKeys.PICKLISTITEM, this.columns, this.OPath)
        .subscribe(
          (data: any) => {
            this.availableRoles = data;
            this.parentId = data[0]["ParentId"];
          },
          (error) => {
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
            console.error(error);
          },
          () => {
            this.appService.showLoader(false);
          },
        ),
    );

    this.subscriptions.push(
      this.dataService.updateGridData$.subscribe((role) => {
        this.availableRoles.pop();
        this.availableRoles.push(role);
        this.rolesGrid.instance.refresh();
      }),
    );
  }

  public onCellPrepared(e) {
    if (e.rowType === "data" && e.column.type === "buttons") {
      e.cellElement.style.cursor = "pointer";
      e.cellElement.innerHTML = DefaultIcons.CONTEXTMENU;
    }
  }

  public onCellClick(e) {
    this.role = e.data;
    this.rowIndex = e.rowIndex;
    if (e.rowType === "data" && e.column.type === "buttons") {
      e.cellElement.className = "context-menu-icon" + e.rowIndex;
      e.cellElement.innerHTML = this.contextMenu.visible
        ? DefaultIcons.CLOSE
        : DefaultIcons.CONTEXTMENU;

      this.contextMenu.target = ".context-menu-icon" + e.rowIndex;
      this.contextMenuItems[0].visible = this.dataService.hasPermission(
        PermissionContext.Roles,
        ActionType.UPDATE,
      );
      this.contextMenuItems[1].visible = this.dataService.hasPermission(
        PermissionContext.Roles,
        ActionType.DELETE,
      );
      this.contextMenu.instance.show();
    }
  }

  public openEditDialog(): void {
    this.rolesGrid.instance.editRow(this.rowIndex);
  }

  public openRemoveDialog(): void {
    this.confirmDeletePopup.instance.show();
  }

  public assignNewRole(): void {
    this.isUpdating = false;
    this.rolesGrid.instance.addRow();
  }

  public saveRow(e): void {
    if (e.changes[0].type !== "remove") {
      if (e.changes[0].data.Value) {
        this.role = new Role(null);
        if (e.changes[0].key["Id"]) this.role.id = e.changes[0].key["Id"];
        this.role.name = e.changes[0].data["Value"];
        this.role.parentId = this.parentId;
        this.isUpdating = e.changes[0].type === "update" ? true : false;
        if (!this.isUpdating) {
          e.cancel = false;
        }
        this.persistRole();
      } else {
        this.notificationService.callNotification({
          message: NotificationMessage.Empty,
          type: ToastType.INFO,
        });
        e.cancel = true;
      }
      this.rolesGrid.instance.refresh();
    }
  }

  private persistRole(): void {
    const rolePersistQuery: TargetObjectData = new TargetObjectData();
    rolePersistQuery.ObjectKey = new ObjectKey();
    rolePersistQuery.ObjectKey.ObjectType = ObjectKeys.PICKLISTITEM;
    const roleColumns: TargetColumnValue[] = [
      { Name: "Value", Value: this.role.name },
      { Name: "Description", Value: this.role.name },
      { Name: "ParentId", Value: this.role.parentId },
    ];
    if (this.isUpdating === true) {
      rolePersistQuery.Mode = PersistMode.Update;
      rolePersistQuery.ObjectKey.Id = this.role.id;
    } else {
      rolePersistQuery.Mode = PersistMode.Insert;
    }
    rolePersistQuery.TargetColumns = roleColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = rolePersistQuery;
    this.subscriptions.push(
      this.persistenceService
        .executePersistObjectQuery(persistObject)
        .subscribe(
          (data: any) => {
            this.getRoleById(this.columns, data.Id);
          },
          (error) => {
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
          },
        ),
    );
  }

  private getRoleById(columns: QueryColumn[], id: string) {
    const opath = "Id=" + "'" + id + "'";
    this.subscriptions.push(
      this.dataService
        .readObjects(ObjectKeys.PICKLISTITEM, columns, opath)
        .subscribe(
          (data) => {
            if (this.isUpdating === true) {
              const selectedIndex = this.availableRoles.findIndex((item) => {
                item.id == id;
              });
              const clonedItem = { ...this.role };
              this.availableRoles[selectedIndex] = clonedItem;
            } else {
              this.dataService.updateGridData(data[0]);
            }
            this.notificationService.callNotification({
              message: NotificationMessage.ValidUpdateMessage,
              type: ToastType.SUCCESS,
            });
          },
          (error) => {
            this.notificationService.callNotification({
              message: NotificationMessage.Error,
              type: ToastType.ERROR,
            });
            console.error(error);
          },
        ),
    );
  }

  public removeRow(): void {
    this.isUpdating = true;
    const roleId = this.role.Id;
    this.subscriptions.push(
      this.dataService.deleteObject(ObjectKeys.PICKLISTITEM, roleId).subscribe(
        () => {
          this.notificationService.callNotification({
            message: NotificationMessage.ValidDeleteMessage,
            type: ToastType.SUCCESS,
          });
        },
        (error) => {
          console.error(error);
          this.notificationService.callNotification({
            message: NotificationMessage.Error,
            type: ToastType.ERROR,
          });
        },
      ),
    );
    const index = this.availableRoles.findIndex((item) => item.Id === roleId);
    this.availableRoles.splice(index, 1);
    this.rolesGrid.instance.refresh();

    this.confirmDeletePopup.instance.hide();
  }

  public ngOnDestroy(): void {
    this.subscriptions.map((subscription) => {
      subscription.unsubscribe();
    });
  }

  abortDelete() {
    this.confirmDeletePopup.instance.hide();
  }
}

export class Role {
  id: string;
  parentId: string;
  name: string;
  Id: string;
  constructor(data) {
    Object.assign(this, data);
  }
}
